/**
 * How an AI Service is NAMED on screen (task 784).
 *
 * One rule, one place: `name → description → method + path`.
 *
 *   1. **`name`** — the service's own title (added service-side by the task
 *      numbered 786). Display-only: invocation still resolves on
 *      `(accountSlug, path)`, so renaming a service never moves its endpoint.
 *   2. **`description`** — what every surface used as a title before `name`
 *      existed. Keeping it as the second rung is what makes the field's arrival
 *      invisible to the services nobody has named yet.
 *   3. **`method + path`** — the last resort, so a label is never blank. A
 *      service with neither a name nor a description still reads as something a
 *      User can recognise in a list.
 *
 * The path here is deliberately the BARE stored path, not the slug-qualified
 * one. Every row in a list belongs to one account, so repeating the same slug on
 * every line is noise; slug qualification is reserved for the surfaces that
 * answer "how do I call this?" — the header's method+path chip, the Connect
 * tab's URL and snippets, the API-docs spec, and every copy affordance. That
 * split is also what stops the detail header from printing the same string twice
 * when a service has no name.
 *
 * The parameter type is structural rather than `Endpoint` so the pickers and run
 * rows that hold a partial endpoint shape can call it without inventing a cast.
 */

/** Anything that can be labelled as an endpoint: the four fields the rule reads. */
export interface EndpointLabelParts {
  name?: string | null;
  description?: string | null;
  method?: string | null;
  path?: string | null;
}

const trimmed = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * The display label for an AI Service. Never empty: an endpoint with no name, no
 * description and no method still answers with its path, and one with nothing at
 * all answers with the placeholder rather than a blank cell.
 */
export function endpointDisplayName(endpoint: EndpointLabelParts | null | undefined): string {
  if (!endpoint) return 'Untitled AI Service';

  const name = trimmed(endpoint.name);
  if (name) return name;

  const description = trimmed(endpoint.description);
  if (description) return description;

  const method = trimmed(endpoint.method);
  const path = trimmed(endpoint.path).replace(/^\/+/, '').replace(/\/+$/, '');
  const fallback = [method, path].filter(Boolean).join(' ');

  return fallback || 'Untitled AI Service';
}

export default endpointDisplayName;
